import { Injectable } from '@angular/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Assets, GetDownloadAssetByIdAndTypeRequest, AssetReferenceType } from '@itero/assets-bff-sdk';
import { TimberService } from '@logging/timber.service';
import { PlatformServiceNames } from '@shared/store/IShellContext';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, map, switchMap, take, timeout } from 'rxjs';
import { saveAs } from 'file-saver';
import { ModalIcon, ModalKeys, NotificationService } from '@shared/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { SoftwareOptionsService } from '@core/companySoftwareOptions/service/softwareOptions.service';
import { SoftwareOptionsForCompany } from '@shared/enums';


@Injectable({
    providedIn: 'root',
})
export class AssetsSdkService {
    private assetManager: Assets | null = null;
    public assetManagerInitialized$ = new BehaviorSubject<boolean>(false);
    private accessToken: string | null = null;

    constructor(
        private readonly shellContextService: ShellContextService,
        private readonly eupRoutesService: EupRoutesService,
        private readonly timberService: TimberService,
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private globalSettings: GlobalSettingsService,
        private softwareOptionsService: SoftwareOptionsService
    ) {
        this.notificationService = notificationService;
        this.translateService = translateService;
        this.ensureInitialized();
        this.listenToAccessToken();
    }

    /**
   * Ensures the asset manager is initialized with the proper configuration.
   * If already initialized, this method does nothing.
   */
    private ensureInitialized(): void {
        this.shellContextService.isContextReady().pipe(
            filter((isReady) => isReady),
            take(1),
            switchMap(() => this.shellContextService.getContext().pipe(take(1)))
        ).subscribe((context) => {
            const assetsManagerUrl = context.platformServices.find(
                (service) => service.name === PlatformServiceNames.AssetManager
            )?.url;

            if (!assetsManagerUrl) {
                return;
            }

            const web3dToolUrl = this.eupRoutesService.rx?.onlineOrthoViewerUrl;

            const allowedOrigins = web3dToolUrl
                ? [new URL(web3dToolUrl).origin]
                : [];

            this.assetManager = new Assets({
                assetsBffUrl: assetsManagerUrl,
                isParent: true,
                allowedOrigins,
                useTransferables: true,
            });

            this.assetManagerInitialized$.next(true);
        });
    }

    private listenToAccessToken(): void {
        this.shellContextService.getContext().pipe(
            map((context) => context.security.accessToken),
            distinctUntilChanged()
        ).subscribe((accessToken) => {
            this.accessToken = accessToken;
        });
    }

    private async getAssetManager(): Promise<Assets> {
        if (!this.assetManager) {
            await firstValueFrom(
                this.assetManagerInitialized$.pipe(
                    filter(Boolean),
                    take(1),
                    timeout(15_000)
                )
            );
        }

        if (!this.assetManager || !this.accessToken) {
            throw new Error('Assets SDK is not initialized');
        }

        return this.assetManager;
    }

    async getAssetDownloadLink(request: GetDownloadAssetByIdAndTypeRequest) {
        const assetManager = await this.getAssetManager();

        return assetManager.getAssetDownloadLink(
            request,
            this.accessToken!
        );
    }

    async fileExists(request: GetDownloadAssetByIdAndTypeRequest): Promise<boolean> {
        try {
            if (!this.assetManager || !this.accessToken) {
                return false;
            }

            const file = await this.getAssetDownloadLink(request);
            return file.isFound && file.hasContent;

        } catch (error) {
            this.timberService.error(`Failed to check if file exists ${error}`, { module: 'AssetsSdkService' });
            return false;
        }
    }

    async downloadGalleryAsZip(rxId: string, fileName: string, errorBodyKey: string): Promise<boolean> {
        try {
            if (!this.assetManager || !this.accessToken || !rxId) {
                this.timberService.error(`Asset Manager not initialized, access token missing, or rxId missing`, { module: 'AssetsSdkService' });
                return false;
            }
            const assetRequest = { rxId: rxId.toString(), assetReferenceType: AssetReferenceType.CaaS_IMG };
            const file = await this.assetManager.getAsset(assetRequest, this.accessToken, { forceFetch: true });
            saveAs(new Blob([file]), fileName);
            return true;

        } catch (error) {
            this.timberService.error(`Failed to download gallery images ${error}`, { module: 'AssetsSdkService' });
            this.notificationService.show(
                this.translateService.instant('Errors.Error_download_file_title'),
                this.translateService.instant(errorBodyKey),
                { buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
            );
            return false;
        }
    }

    public isUseAssetsSdkSwoEnabled(): boolean {
        const companySoftwareOptions = this.globalSettings.get().companySoftwareOptions;
        return this.softwareOptionsService.isSoftwareOptionExists(
            SoftwareOptionsForCompany.cswo_UseAssetsSdk,
            companySoftwareOptions
        );
    }
}
